/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { ICorrespondenceConfig } from 'app/core/models/essette-interfaces';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import { Correspondence } from 'app/core/components/correspondence/correspondence';
import { AuthChangeEvent, AuthCorrespondenceEvent } from 'app/um/lib/events';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class AuthCorrespondencePage extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    protected authId!: string;
    protected readonly: boolean = false;
    protected canEditClosedAuth: boolean = false;
    protected hasAuthRoute = false;
    protected memberId: string | null = null;

    protected correspondenceConfig: ICorrespondenceConfig = {
        transitionalPageId: '604',
        moduleCode: 'UM',
        serviceRootUrl: 'um/auths',
        rollupRootUrl: 'um/auths/correspondence',
        targetType: 'UM.Auth',
        addTargetProperty: this.addAuthProperty.bind(this),
        targetColumnHeader: 'Auth #',
        targetColumnProperty: 'auth.number',
        showTargetColumn: false,
        targetInclude: ['auth', 'task', 'attachedTo.type'],
        previewTransitionalPageId: '4054',
        rollupByMember: false,
        targetSummaryTooltip: 'um:auth.summary.$long'
    }

    private subscriptions: Array<Subscription> = [];


    ////****************** Element References ***************************************////
    protected $correspondence!: Correspondence;


    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly context: AuthPageContext,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async canActivate(params: { authId: string }): Promise<boolean> {
        if(!params.authId) { throw new Error('Missing or invalid authId parameter'); }

        return true;
    }

    protected async activate(params: { authId: string }): Promise<void> {
        if(this.authId !== params.authId) { this.authId = params.authId; }

        this.checkPermissions();

        this.memberId = this.context.auth?.member?.id || null;

        this.subscriptions.push(
            this.events.subscribe(AuthChangeEvent, (e: AuthChangeEvent) => this.handleAuthChangeEvent(e)),
            this.events.subscribe(AuthCorrespondenceEvent, (e: AuthChangeEvent) => this.handleAuthChangeEvent(e))
        );
    }

    protected deactivate(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }


    ////****************** Events ***************************************************////
    protected handleAuthChangeEvent(e: AuthChangeEvent): void {
        if(e && e.authReloaded) {
            this.checkPermissions();
        };
        this.$correspondence.reload();
    }


    ////****************** Functions *************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        const canEditAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditAuth');
        this.canEditClosedAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditClosedAuth');
        this.readonly = this.context?.authIsReadOnly(canEditAuth, this.canEditClosedAuth);
        this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
        if(this.accountContext.hasRouteAccess('um.auth.summary')) {
            this.correspondenceConfig.openTargetSummary = this.openSummary.bind(this);
        }
    }

    public addAuthProperty(letter: UM.AuthCorrespondenceLetter): void {
        letter.auth = { id: this.authId };
    }

    public openSummary(letter: UM.AuthCorrespondenceLetter): void {
        if(letter.auth) openAuthSummary(letter.auth, this.hasAuthRoute, this.readonly);
    }
}
